import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { eventService } from '../services/eventService';
import { toast } from 'react-toastify';
import ReactQuill, { Quill } from 'react-quill';
import ImageResize from 'quill-image-resize-module-react';

Quill.register('modules/imageResize', ImageResize);

// Registrar atributos de estilo para imagens
const ImageBlot = Quill.import('formats/image');
ImageBlot.tagName = 'IMG';
ImageBlot.className = undefined;
ImageBlot.attributes = ['alt', 'height', 'width', 'style'];

class CustomImage extends ImageBlot {
  static create(value) {
    const node = super.create(value);
    if (typeof value === 'string') {
      node.setAttribute('src', value);
    }
    return node;
  }

  static formats(domNode) {
    const formats = {};
    if (domNode.hasAttribute('height')) {
      formats.height = domNode.getAttribute('height');
    }
    if (domNode.hasAttribute('width')) {
      formats.width = domNode.getAttribute('width');
    }
    if (domNode.hasAttribute('style')) {
      formats.style = domNode.getAttribute('style');
    }
    if (domNode.hasAttribute('alt')) {
      formats.alt = domNode.getAttribute('alt');
    }
    return formats;
  }

  static value(domNode) {
    return domNode.getAttribute('src');
  }

  format(name, value) {
    if (['height', 'width', 'style', 'alt'].indexOf(name) > -1) {
      if (value) {
        this.domNode.setAttribute(name, value);
      } else {
        this.domNode.removeAttribute(name);
      }
    } else {
      super.format(name, value);
    }
  }
}

Quill.register(CustomImage, true);

const EventForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_date: '',
    duration_days: 1,
    location: '',
    locationLink: '',
    max_attendees: '',
    safety_margin: ''
  });
  const [image, setImage] = useState(null);
  const [currentImage, setCurrentImage] = useState(null);
  
  // Limite de caracteres para LONGTEXT (4GB, mas vamos usar um limite prático)
  const MAX_DESCRIPTION_LENGTH = 100000; // 100k caracteres é um limite razoável
  const descriptionLength = formData.description.length;
  const descriptionPercentage = (descriptionLength / MAX_DESCRIPTION_LENGTH) * 100;

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ],
    imageResize: {
      parchment: Quill.import('parchment'),
      modules: ['Resize', 'DisplaySize', 'Toolbar']
    }
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'color', 'background',
    'align',
    'link', 'image',
    'width', 'height', 'style', 'float'
  ];

  useEffect(() => {
    // Bloquear acesso de colaboradores que não são managers
    if (user?.isCollaborator) {
      if (id && user.role !== 'manager') {
        toast.error('Apenas gerentes podem editar eventos');
        navigate('/dashboard');
        return;
      }
      if (!id) {
        toast.error('Colaboradores não podem criar eventos');
        navigate('/dashboard');
        return;
      }
    }

    if (id) {
      loadEvent();
    }
  }, [id, user, navigate]);

  const loadEvent = async () => {
    try {
      const data = await eventService.getById(id);
      const event = data.event;

      setFormData({
        title: event.title,
        description: event.description,
        start_date: event.start_date.slice(0, 16),
        duration_days: event.duration_days,
        location: event.location,
        locationLink: event.locationLink || '',
        max_attendees: event.max_attendees || '',
        safety_margin: event.safety_margin || ''
      });
      if (event.image_url) {
        setCurrentImage("https://eventozapi.gigadigital.com.br" + event.image_url);
      }
    } catch (error) {
      toast.error('Erro ao carregar evento');
      navigate('/dashboard');
    }
  };

  const handleChange = (e) => {
    console.log("handle change", e.target.name, e.target.value);
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const data = new FormData();
    Object.keys(formData).forEach(key => {
      if (formData[key]) data.append(key, formData[key]);
    });
    if (image) data.append('image', image);

    try {
      if (id) {
        await eventService.update(id, data);
        toast.success('Evento atualizado!');
      } else {
        await eventService.create(data);
        toast.success('Evento criado!');
      }
      navigate('/dashboard');
    } catch (error) {
      toast.error('Erro ao salvar evento');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container" style={{ padding: '2rem 0' }}>
      <div className="card" style={{ maxWidth: '800px', margin: '0 auto' }}>
        <h1>{id ? 'Editar Evento' : 'Novo Evento'}</h1>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Título</label>
            <input
              type="text"
              name="title"
              className="form-input"
              value={formData.title}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Imagem da capa</label>
            {currentImage && (
              <div style={{ marginBottom: '1rem' }}>
                <img 
                  src={currentImage} 
                  alt="Imagem atual do evento" 
                  style={{ 
                    maxWidth: '300px', 
                    maxHeight: '200px', 
                    objectFit: 'cover',
                    borderRadius: '8px',
                    border: '1px solid #ddd'
                  }} 
                />
                <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.5rem' }}>Imagem atual</p>
              </div>
            )}
            <input
              type="file"
              className="form-input"
              accept="image/*"
              onChange={(e) => setImage(e.target.files[0])}
            />
          </div>

          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <label className="form-label" style={{ margin: 0 }}>Descrição</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '0.875rem', color: descriptionPercentage > 90 ? '#e74c3c' : '#666' }}>
                  {descriptionLength.toLocaleString()} / {MAX_DESCRIPTION_LENGTH.toLocaleString()} caracteres
                </span>
                <div style={{ 
                  width: '100px', 
                  height: '8px', 
                  backgroundColor: '#e0e0e0', 
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{ 
                    width: `${Math.min(descriptionPercentage, 100)}%`, 
                    height: '100%', 
                    backgroundColor: descriptionPercentage > 90 ? '#e74c3c' : descriptionPercentage > 75 ? '#f39c12' : '#27ae60',
                    transition: 'all 0.3s ease'
                  }} />
                </div>
              </div>
            </div>
            <div style={{ height: '400px' }}>
              <ReactQuill
                value={formData.description}
                onChange={(value) => {
                  const textLength = value.length;
                  setFormData(prevFormData => ({ ...prevFormData, description: value }))
                  
                  if (textLength >= MAX_DESCRIPTION_LENGTH) {
                    toast.error(
                      <div style={{ lineHeight: '1.6' }}>
                        <strong style={{ fontSize: '1.1rem', display: 'block', marginBottom: '0.5rem' }}>
                          ⚠️ Limite de caracteres atingido!
                        </strong>
                        <p style={{ margin: '0.5rem 0', fontSize: '0.95rem' }}>
                          Você atingiu o limite de <strong>{MAX_DESCRIPTION_LENGTH.toLocaleString()} caracteres</strong>.
                        </p>
                        <div style={{ 
                          backgroundColor: 'rgba(255, 255, 255, 0.1)', 
                          padding: '0.75rem', 
                          borderRadius: '6px',
                          marginTop: '0.75rem',
                          fontSize: '0.9rem'
                        }}>
                          <strong>💡 Dicas para otimizar:</strong>
                          <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem' }}>
                            <li>Reduza o tamanho das imagens inseridas</li>
                            <li>Imagens grandes ocupam muito espaço em pixels</li>
                            <li>Considere resumir textos longos</li>
                            <li>Use formatação em vez de repetir texto</li>
                          </ul>
                        </div>
                      </div>,
                      { 
                        autoClose: 20000,
                        style: { minWidth: '500px' },
                        position: "top-center"
                      }
                    );
                  }
                }}
                modules={quillModules}
                formats={quillFormats}
                theme="snow"
                style={{ height: '350px', marginBottom: '3rem' }}
              />
            </div>
          </div>


          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label className="form-label">Data e Hora</label>
              <input
                type="datetime-local"
                name="start_date"
                className="form-input"
                value={formData.start_date}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Duração (dias)</label>
              <input
                type="number"
                name="duration_days"
                className="form-input"
                value={formData.duration_days}
                onChange={handleChange}
                min="1"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Local</label>
            <input
              type="text"
              name="location"
              className="form-input"
              value={formData.location}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Link da Localização (opcional)</label>
            <input
              type="url"
              name="locationLink"
              className="form-input"
              value={formData.locationLink}
              onChange={handleChange}
              placeholder="https://maps.google.com/..."
            />
            <small style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.25rem', display: 'block' }}>
              <strong>Para exibir o mapa incorporado:</strong> No Google Maps, clique em "Compartilhar" → "Incorporar mapa" e cole o link "src" do iframe.<br/>
              Links curtos (goo.gl) exibirão apenas um botão para abrir o mapa.
            </small>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label className="form-label">Número de Vagas (opcional)</label>
              <input
                type="number"
                name="max_attendees"
                className="form-input"
                value={formData.max_attendees}
                onChange={handleChange}
                min="1"
              />
              <small style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.25rem', display: 'block' }}>
                Deixar em branco para vagas ilimitadas
              </small>
            </div>

            <div className="form-group">
              <label className="form-label">Margem de Segurança (opcional)</label>
              <input
                type="number"
                name="safety_margin"
                className="form-input"
                value={formData.safety_margin}
                onChange={handleChange}
                min="0"
                placeholder="Ex: 10"
              />
              <small style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.25rem', display: 'block' }}>
                Vagas extras além do limite
              </small>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Salvando...' : 'Salvar Evento'}
            </button>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => navigate('/dashboard')}
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventForm;
